<template>
  <section data-testid="cockpit-kpi-section">
    <h2 class="cockpit-section-title cockpit-section-title--first">
      {{ participantMode
        ? $t('UserProjects.cockpit.sections.yourResults')
        : $t('UserProjects.cockpit.sections.projectStatus') }}
    </h2>

    <div
      v-if="participantMode"
      class="grid grid-cols-1 md:grid-cols-2 gap-4"
    >
      <CockpitResultCard
        :label="$t('UserProjects.cockpit.kpi.yourPpc')"
        :value="currentPpcLabel"
        :ampel-dot="currentAmpel"
        :prev-value="prevPpcLabel"
        :delta-value="currentDeltaLabel"
        :n-value="currentN"
        :n-label="$t('UserProjects.cockpit.kpi.commitments')"
        test-id="cockpit-kpi-your-ppc"
      />
    </div>

    <div
      v-else
      class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4"
    >
      <CockpitResultCard
        :label="currentPpcTileLabel"
        :value="currentPpcLabel"
        :ampel-dot="currentAmpel"
        :prev-value="prevPpcLabel"
        :delta-value="currentDeltaLabel"
        :n-value="currentN"
        :n-label="$t('UserProjects.cockpit.kpi.commitments')"
        clickable
        test-id="cockpit-kpi-ppc"
        @click="$emit('open-ppc-drawer')"
      />
      <CockpitResultCard
        :label="avgPpcTileLabel"
        :value="avgPpcLabel"
        :ampel-dot="avgAmpel"
        :trend="avgTrendLabel"
        trend-color="#6B6B6B"
        :n-value="avgN"
        :n-label="$t('UserProjects.cockpit.kpi.commitments')"
        :sub="avgSubLabel"
        test-id="cockpit-kpi-avg-ppc"
      />
      <CockpitResultCard
        :label="$t('UserProjects.cockpit.kpi.milestones')"
        :trend="milestoneTrend"
        :trend-color="milestoneTrendColor"
        clickable
        test-id="cockpit-kpi-milestones"
        @click="$emit('go-milestones')"
      >
        <div class="flex items-center gap-2 mb-2">
          <span class="cockpit-chip cockpit-chip--green">{{ milestoneChips.green }} ✓</span>
          <span class="cockpit-chip cockpit-chip--yellow">{{ milestoneChips.yellow }} ⚠</span>
          <span class="cockpit-chip cockpit-chip--muted">{{ milestoneChips.red }} ✗</span>
        </div>
      </CockpitResultCard>
      <CockpitResultCard
        :label="$t('UserProjects.cockpit.kpi.openConstraints')"
        :value="String(openConstraintsTotal)"
        :trend="constraintsTrend"
        :trend-color="constraintsTrendColor"
        clickable
        test-id="cockpit-kpi-constraints"
        @click="$emit('go-constraints')"
      />
    </div>
  </section>
</template>

<script>
import CockpitResultCard from './CockpitResultCard.vue';
import {
  formatPpcDelta,
  resolvePpcAmpelHex,
} from '@/utils/cockpitDisplay';

export default {
  name: 'CockpitKpiSection',
  components: { CockpitResultCard },
  props: {
    participantMode: { type: Boolean, default: false },
    current: { type: Object, default: null },
    history: { type: Array, default: () => [] },
    averagePpc: { type: Number, default: null },
    ppcPeriod: { type: String, default: 'week' },
    thresholds: { type: Object, default: () => ({ greenMin: 80, yellowMin: 60 }) },
    milestoneChips: { type: Object, default: () => ({ green: 0, yellow: 0, red: 0 }) },
    openConstraintsTotal: { type: Number, default: 0 },
  },
  emits: ['open-ppc-drawer', 'go-milestones', 'go-constraints'],
  computed: {
    currentPpc() {
      return this.current?.ppc ?? null;
    },
    currentN() {
      return this.current?.n ?? 0;
    },
    currentPpcLabel() {
      return this.currentPpc === null ? '—' : `${this.currentPpc}%`;
    },
    prevPpc() {
      if (this.history.length < 2) return null;
      return this.history[this.history.length - 2]?.ppc ?? null;
    },
    prevPpcLabel() {
      return this.prevPpc === null ? '' : `${this.prevPpc}%`;
    },
    currentDelta() {
      return this.current?.deltaVsPrevious
        ?? this.current?.deltaVsPreviousWeek
        ?? null;
    },
    currentDeltaLabel() {
      return formatPpcDelta(this.currentDelta) || '';
    },
    currentAmpel() {
      return resolvePpcAmpelHex(this.currentPpc, this.thresholds) || '';
    },
    currentPpcTileLabel() {
      return this.ppcPeriod === 'month'
        ? this.$t('UserProjects.cockpit.kpi.ppcThisMonth')
        : this.$t('UserProjects.cockpit.kpi.ppcThisWeek');
    },
    avgPpcTileLabel() {
      return this.ppcPeriod === 'month'
        ? this.$t('UserProjects.cockpit.kpi.avgPpcMonths')
        : this.$t('UserProjects.cockpit.kpi.avgPpcWeeks');
    },
    avgPpcLabel() {
      return this.averagePpc === null ? '—' : `${this.averagePpc}%`;
    },
    avgAmpel() {
      return resolvePpcAmpelHex(this.averagePpc, this.thresholds) || '';
    },
    avgN() {
      return this.currentN * (this.ppcPeriod === 'month' ? 2 : 8);
    },
    avgTrendLabel() {
      return this.$t('UserProjects.cockpit.kpi.stable');
    },
    avgSubLabel() {
      if (this.averagePpc === null) return '';
      const target = this.thresholds.greenMin ?? 80;
      const gap = this.averagePpc - target;
      const sign = gap >= 0 ? '+' : '';
      return this.$t('UserProjects.cockpit.kpi.targetGap', { target, gap: `${sign}${gap}` });
    },
    milestoneTrend() {
      if (this.milestoneChips.yellow > 0) {
        return this.$t('UserProjects.cockpit.kpi.milestonesAtRisk');
      }
      if (this.milestoneChips.red > 0) {
        return this.$t('UserProjects.cockpit.kpi.milestonesOverdue');
      }
      return this.$t('UserProjects.cockpit.kpi.milestonesOnTrack');
    },
    milestoneTrendColor() {
      if (this.milestoneChips.red > 0) return '#EF4444';
      if (this.milestoneChips.yellow > 0) return '#EAB308';
      return '#22C55E';
    },
    constraintsTrend() {
      return this.$t('UserProjects.cockpit.kpi.openConstraintsHint');
    },
    constraintsTrendColor() {
      return this.openConstraintsTotal > 0 ? '#EF4444' : '#6B6B6B';
    },
  },
};
</script>

<style scoped>
.cockpit-section-title {
  margin: 1.75rem 0 0.75rem;
  font-size: 13px;
  color: #ababab;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-weight: 500;
}

.cockpit-section-title--first {
  margin-top: 0;
}

.cockpit-chip {
  display: inline-flex;
  align-items: center;
  padding: 0.125rem 0.5rem;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 500;
}

.cockpit-chip--green {
  background: #f0fdf4;
  color: #22c55e;
}

.cockpit-chip--yellow {
  background: #fefce8;
  color: #eab308;
}

.cockpit-chip--muted {
  background: #f5f5f5;
  color: #ababab;
}
</style>
